import { CustomCellRendererProps } from "@ag-grid-community/react";
import { HexType } from "@hex/common";
import React from "react";
import styled from "styled-components";

import { HexTooltip } from "../../../../hex-components/HexTooltip.js";
import { typedMemo } from "../../../../util/typedMemo.js";
import { getOwnerName } from "../../../hex-list-2/hex-row/utils.js";
import { ProjectsTableRowData } from "../../types.js";
import { RowHeaderTableCellText, TableRowProjectTypeIcon } from "../styled.js";

type ReadonlyNameCellRendererOptions = Pick<
  ProjectsTableRowData,
  "hexType" | "creator" | "owner"
>;

export interface ReadonlyNameCellRendererProps<DataType>
  extends CustomCellRendererProps<DataType> {
  mapDataToHex: (data: DataType) => ReadonlyNameCellRendererOptions;
}

export const ReadonlyNameCellRenderer = typedMemo(
  function ReadonlyNameCellRenderer<D>({
    context: { canSelect },
    data,
    mapDataToHex,
  }: ReadonlyNameCellRendererProps<D>) {
    if (data == null) {
      return null;
    }
    const hex = mapDataToHex(data);
    const ownerName = getOwnerName(hex);
    const isComponent = hex.hexType === HexType.COMPONENT;
    const text = `${isComponent ? "Component" : "Project"} ${`owned by ${ownerName}`}`;
    return (
      <StyledHexTooltip
        content={`This ${
          isComponent ? "component" : "project"
        } has not been shared with you.`}
        maxWidthOverride={250}
      >
        <ReadonlyNameContainer>
          {!canSelect && (
            <TableRowProjectTypeIcon
              hexType={hex.hexType}
              isUnknownHex={true}
            />
          )}
          <RowHeaderTableCellText $isUnknownHex={true}>
            {text}
          </RowHeaderTableCellText>
        </ReadonlyNameContainer>
      </StyledHexTooltip>
    );
  },
);

const ReadonlyNameContainer = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  cursor: not-allowed;

  ${TableRowProjectTypeIcon} {
    padding-right: 8px;
  }
`;

const StyledHexTooltip = styled(HexTooltip)`
  height: 100%;
`;
